/**
 * App-wide state: auth status, in-memory vault session, decrypted items.
 *
 * The session keys (`vaultSession`) are kept here in memory only; they are wiped
 * on lock/logout. Items are decrypted in-browser and never sent to the server.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { api, setCsrfToken } from '../api/client.js';
import {
  clientLogin,
  clientRegister,
  decryptItems,
  encryptNewItem,
  wipeSession,
  MfaRequiredError,
  type DecryptedItem,
  type EncryptedItemDto,
  type PublicUser,
  type SecuritySettings,
  type VaultSession,
} from '../crypto/session.js';
import type { ItemFields } from '@battini/crypto';
import { deriveEncKey, deriveMasterKey, fromBase64, loadPrivateKey, unwrapKey } from '@battini/crypto';

type AuthStatus = 'loading' | 'guest' | 'locked' | 'unlocked';

interface AppState {
  status: AuthStatus;
  user: PublicUser | null;
  security: SecuritySettings | null;
  items: DecryptedItem[];
  vaultSession: VaultSession | null;
  error: string | null;
  refresh: () => Promise<void>;
  register: (email: string, password: string, name?: string) => Promise<{ verificationRequired: boolean }>;
  login: (email: string, password: string, mfaCode?: string) => Promise<void>;
  unlock: (password: string) => Promise<void>;
  lock: () => void;
  logout: () => Promise<void>;
  addItem: (fields: ItemFields) => Promise<void>;
  updateItem: (id: string, fields: ItemFields, version: number) => Promise<void>;
  deleteItem: (id: string, purge?: boolean) => Promise<void>;
  restoreItem: (id: string) => Promise<void>;
  toggleFavourite: (id: string, fav: boolean) => Promise<void>;
  markUsed: (id: string) => Promise<void>;
  reloadSecurity: () => Promise<void>;
  clearError: () => void;
}

const Ctx = createContext<AppState | null>(null);

export function AppProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<PublicUser | null>(null);
  const [security, setSecurity] = useState<SecuritySettings | null>(null);
  const [items, setItems] = useState<DecryptedItem[]>([]);
  const sessionRef = useRef<VaultSession | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!sessionRef.current) return;
    const res = await api.get<{ items: EncryptedItemDto[] }>('/vault/items');
    const decrypted = await decryptItems(sessionRef.current, res.body.items);
    setItems(decrypted);
  }, []);

  // On mount: determine auth status via the session cookie.
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get<{ user: PublicUser; security: SecuritySettings | null }>('/auth/me');
        setUser(res.body.user);
        setSecurity(res.body.security);
        setStatus('locked'); // authenticated, but vault locked (keys not in memory)
      } catch {
        setStatus('guest');
      }
    })();
  }, []);

  const register = useCallback(async (email: string, password: string, name?: string) => {
    setError(null);
    return clientRegister(email, password, name);
  }, []);

  const login = useCallback(async (email: string, password: string, mfaCode?: string) => {
    setError(null);
    try {
      const { user: u, session, security: sec } = await clientLogin(email, password, mfaCode);
      sessionRef.current = session;
      setUser(u);
      setSecurity(sec);
      setStatus('unlocked');
      await refresh();
    } catch (e) {
      if (e instanceof MfaRequiredError) throw e;
      setError(e instanceof Error ? e.message : 'Login failed');
      throw e;
    }
  }, [refresh]);

  const unlock = useCallback(async (password: string) => {
    setError(null);
    if (!user) throw new Error('No authenticated user');
    // Fetch wrapped keys + KDF params, derive locally, unlock.
    const keys = await api.get<{ salt: string; kdfParams: unknown; protectedVaultKey: string; protectedPrivateKey: string }>('/auth/vault-keys');
    const masterKey = await deriveMasterKey(password, fromBase64(keys.body.salt), keys.body.kdfParams as never);
    const encKey = await deriveEncKey(masterKey);
    const symKey = await unwrapKey(encKey, keys.body.protectedVaultKey);
    const privateKey = await loadPrivateKey(symKey, keys.body.protectedPrivateKey);
    sessionRef.current = { masterKey, encKey, symKey, privateKey };
    setStatus('unlocked');
    await refresh();
  }, [user, refresh]);

  const lock = useCallback(() => {
    wipeSession(sessionRef.current);
    sessionRef.current = null;
    setItems([]);
    setStatus('locked');
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      /* ignore */
    }
    setCsrfToken(null);
    wipeSession(sessionRef.current);
    sessionRef.current = null;
    setItems([]);
    setUser(null);
    setSecurity(null);
    setStatus('guest');
  }, []);

  const addItem = useCallback(async (fields: ItemFields) => {
    if (!sessionRef.current) throw new Error('Vault locked');
    const enc = await encryptNewItem(sessionRef.current, fields);
    await api.post('/vault/items', enc);
    await refresh();
  }, [refresh]);

  const updateItem = useCallback(async (id: string, fields: ItemFields, version: number) => {
    if (!sessionRef.current) throw new Error('Vault locked');
    const enc = await encryptNewItem(sessionRef.current, fields);
    await api.put(`/vault/items/${id}`, { ...enc, version });
    await refresh();
  }, [refresh]);

  const deleteItem = useCallback(async (id: string, purge = false) => {
    await api.del(`/vault/items/${id}${purge ? '?purge=1' : ''}`);
    await refresh();
  }, [refresh]);

  const restoreItem = useCallback(async (id: string) => {
    await api.post(`/vault/items/${id}/restore`);
    await refresh();
  }, [refresh]);

  const toggleFavourite = useCallback(async (id: string, fav: boolean) => {
    await api.post(`/vault/items/${id}/favourite`, { favourite: fav });
    await refresh();
  }, [refresh]);

  const markUsed = useCallback(async (id: string) => {
    await api.post(`/vault/items/${id}/used`).catch(() => undefined);
  }, []);

  const reloadSecurity = useCallback(async () => {
    const res = await api.get<{ security: SecuritySettings | null }>('/auth/me');
    setSecurity(res.body.security);
  }, []);

  const value = useMemo<AppState>(() => ({
    status, user, security, items,
    vaultSession: sessionRef.current,
    error,
    refresh, register, login, unlock, lock, logout,
    addItem, updateItem, deleteItem, restoreItem, toggleFavourite, markUsed,
    reloadSecurity, clearError: () => setError(null),
  }), [status, user, security, items, error, refresh, register, login, unlock, lock, logout, addItem, updateItem, deleteItem, restoreItem, toggleFavourite, markUsed, reloadSecurity]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useApp(): AppState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}
